import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addBookmark, deleteBookmark, getBookmarks, type Bookmark } from '../../transport/api'
import { ApiError } from '../../transport/http'

/**
 * AN AGENT'S BOOKMARKS (owner, 2026-10-31: 每一個 Message Block 下面有一個書簽的按鈕 … 收進書簽裡面 … 右側欄位 … 書籤的瀏覽
 * list). One list per agent, read from the agent's own machine (avengents-server bookmarks.ts) — so the buttons under
 * Clean View's messages, the count on the corner button and the list in the drawer are one query, and a bookmark made
 * on the phone is the desktop's too (the drawer re-reads when it opens).
 *
 * A machine whose avengents predates bookmarks answers the route with 404 `not_found`: then `supported` is false and
 * nothing offers a bookmark there — a button that can only fail is worse than none.
 */
export type BookmarkAgent = { session: string; host?: string | null; device_id?: string | null }

/** The edge has no such route: an avengents from before bookmarks. */
export function isOldEdge(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404 && (e.body as { error?: unknown } | undefined)?.error === 'not_found'
}

const bookmarksKey = (a: BookmarkAgent | null) => ['bookmarks', a?.device_id ?? '', a?.host ?? '', a?.session ?? ''] as const

/** One agent's list as a query — the drawer's and More › Bookmarks' (shell/BookmarksPage), one cache entry. */
export function bookmarksQuery(agent: BookmarkAgent | null) {
  return {
    queryKey: bookmarksKey(agent),
    queryFn: async () => (await getBookmarks(agent!.session, agent!.host ?? null, agent?.device_id ?? undefined)).bookmarks ?? [],
    enabled: !!agent,
    staleTime: 15_000,
    retry: (n: number, e: unknown) => !isOldEdge(e) && n < 2,
  }
}

export type NewBookmark = Omit<Bookmark, 'id' | 'created_ts' | 'session' | 'host'>

export function useBookmarks(agent: BookmarkAgent | null) {
  const qc = useQueryClient()
  const key = bookmarksKey(agent)
  const device = agent?.device_id ?? undefined
  const q = useQuery(bookmarksQuery(agent))
  const add = useMutation({
    mutationFn: (b: NewBookmark) => addBookmark({ ...b, session: agent!.session, host: agent!.host ?? null }, device),
    // Drawn at once; the machine's answer replaces it (or, refused, takes it away again).
    onMutate: async (b) => {
      await qc.cancelQueries({ queryKey: key })
      const before = qc.getQueryData<Bookmark[]>(key)
      const draft: Bookmark = { ...b, id: `pending:${b.file}:${b.index}`, session: agent!.session, host: agent!.host ?? null, created_ts: Math.floor(Date.now() / 1000) }
      qc.setQueryData<Bookmark[]>(key, [draft, ...(before ?? [])])
      return { before }
    },
    onError: (_e, _b, ctx) => qc.setQueryData(key, ctx?.before),
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => deleteBookmark(id, device),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key })
      const before = qc.getQueryData<Bookmark[]>(key)
      qc.setQueryData<Bookmark[]>(key, (before ?? []).filter((b) => b.id !== id))
      return { before }
    },
    onError: (_e, _id, ctx) => qc.setQueryData(key, ctx?.before),
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  })
  return {
    list: q.data ?? [],
    /** Known to work on this machine: the list has been read (a later failed re-read does not take the buttons away). */
    supported: q.data !== undefined,
    loading: q.isLoading,
    error: q.error && !isOldEdge(q.error) ? q.error : null,
    refetch: () => void q.refetch(),
    /** The mutations' own functions — stable, so a context built on them holds still. */
    add: add.mutate,
    remove: remove.mutate,
    /** The last write the machine refused, for the person to be told. */
    writeError: add.error ?? remove.error ?? null,
  }
}
